import { generateID, nullishFilter, isNullish, asyncTimeout } from '@caresend/utils';
import update from 'immutability-helper';
import isEqual from 'lodash.isequal';
import mapboxGl from 'mapbox-gl';
import { defineComponent, ref, watch, onMounted } from 'vue';
import __vue_component__$2 from '../../alerts/Alert.vue.js';
import __vue_component__ from '../../buttons/ButtonComponent/ButtonComponent.vue.js';
import __vue_component__$1 from '../Loading/Loading.vue.js';
import { fitToBounds, getCurrentPosition, removeSources, processSources, addUserMapMarker, getCenterAndZoom } from './helpers/index.js';
import { getMapViewComputedStyles } from './helpers/computedStyles.js';
import { buildDefaultMarker } from './helpers/markerBuilders.js';
import { isPolygonOrMultiPolygon, getPointProperties, getPointMapSourceCoordinates, roundedLatLng } from './helpers/private/index.js';
import { mapViewEmits } from './helpers/private/model.js';
import { useElementDimensions } from '../../../composables/dom.js';
import { getStore } from '../../../store/helpers/initStore.js';
import '../../../router/helpers/initRouter.js';
import '@caresend/types';
import { MAPBOX_STYLE_LIGHT_EXAS, MAPBOX_STYLE_LIGHT, MAPBOX_STYLE_DARK, MAPBOX_TOKEN } from '../../../database/mapboxConfig/index.js';
import { isExactTheme } from '../../../store/modules/app/helpers.js';

var script = defineComponent({
    name: 'MapView',
    components: {
        ButtonComponent: __vue_component__,
        Loading: __vue_component__$1,
        Alert: __vue_component__$2,
    },
    props: {
        buildMarker: {
            type: Function,
            default: buildDefaultMarker,
        },
        callbacks: {
            type: Object,
            default: () => ({}),
        },
        centerUser: Boolean,
        customPadding: {
            type: [Number, Object],
            default: null,
        },
        displayPopUp: {
            type: Boolean,
            default: false,
        },
        displayUser: Boolean,
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Disable grouping if it is not desired or is not supported by a custom
         * marker builder.
         */
        disableGrouping: Boolean,
        disabledMessage: {
            type: String,
            default: 'Map is disabled',
        },
        hideLoading: {
            type: Boolean,
            default: false,
        },
        expandable: Boolean,
        groupingThreshold: {
            type: Number,
            default: 0.0001,
        },
        hideControlButtons: Boolean,
        highlightID: {
            type: String,
            default: null,
        },
        initialZoom: {
            type: Number,
            default: () => undefined,
        },
        initialCenter: {
            type: Object,
            default: () => undefined,
        },
        interactive: {
            type: Boolean,
            default: true,
        },
        mapStyle: {
            type: String,
            default: 'light',
        },
        maxZoomInitialTransition: {
            type: Number,
            default: 15,
        },
        minimizedHeight: {
            type: Number,
            default: 200,
        },
        paddingBottom: {
            type: Number,
            default: 40,
        },
        selectedID: {
            type: String,
            default: null,
        },
        sources: {
            type: Array,
            default: () => [],
        },
    },
    emits: mapViewEmits,
    setup(props, { emit }) {
        const store = getStore();
        const getMapStyle = (mapStyle) => {
            const lightStyle = isExactTheme() ? MAPBOX_STYLE_LIGHT_EXAS : MAPBOX_STYLE_LIGHT;
            return mapStyle === 'dark' ? MAPBOX_STYLE_DARK : lightStyle;
        };
        const token = MAPBOX_TOKEN;
        const style = getMapStyle(props.mapStyle);
        const mapContainerID = generateID();
        const mapsByContainerName = {};
        const mapViewRef = ref(null);
        const mapViewContainerRef = ref(null);
        const { width, height } = useElementDimensions(mapViewRef);
        /** Wait for any animations or transitions to complete before triggering a resize */
        let resizeTimeout;
        watch([width, height], ([w, h], prev) => {
            if (w && h && !isEqual(prev, [w, h])) {
                if (resizeTimeout)
                    clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(() => {
                    Object.values(mapsByContainerName).forEach((map) => {
                        map.resize();
                    });
                }, 300);
            }
        });
        const isLoading = ref(false);
        const isAlertMapLoading = ref(false);
        const userLngLat = ref(undefined);
        const currentBounds = ref(null);
        const setCurrentBounds = (containerName) => {
            var _a, _b;
            currentBounds.value = (_b = (_a = mapsByContainerName[containerName]) === null || _a === void 0 ? void 0 : _a.getBounds()) !== null && _b !== void 0 ? _b : null;
            emit('setBounds', currentBounds.value);
        };
        /**
         * Returns map sources with `hovered` and `highlight` states based on props.
         */
        const getSourcesWithState = (sources) => sources.map((source) => {
            const sourceProperties = getPointProperties(source);
            if (!sourceProperties)
                return source;
            const sourcePropertiesWithState = update(sourceProperties, {
                $merge: {
                    highlight: sourceProperties.id === props.highlightID,
                    selected: sourceProperties.id === props.selectedID,
                },
            });
            const dataWithState = source.data.map((dataPoint) => ({
                ...dataPoint,
                properties: sourcePropertiesWithState,
            }));
            return { ...source, data: dataWithState };
        });
        const loadMap = async ({ containerName, displayControl, }) => {
            var _a, _b, _c;
            let center = props.initialCenter;
            let zoom = props.initialZoom;
            if (!center || isNullish(zoom)) {
                const centerAndZoom = await getCenterAndZoom(props.displayUser || props.centerUser);
                center = center !== null && center !== void 0 ? center : centerAndZoom.coords;
                zoom = zoom !== null && zoom !== void 0 ? zoom : centerAndZoom.zoom;
            }
            const sources = getSourcesWithState(props.sources);
            const container = containerName !== null && containerName !== void 0 ? containerName : `mapContainer-${mapContainerID}`;
            mapsByContainerName[container] = new mapboxGl.Map({
                container,
                accessToken: MAPBOX_TOKEN,
                style,
                center,
                zoom,
                interactive: props.interactive,
            });
            (_a = mapsByContainerName[container]) === null || _a === void 0 ? void 0 : _a.on('load', async () => {
                var _a;
                /**
                 * There appears to be a race condition in this logic. Sometimes the
                 * logic appears to be executed before the DOM is ready and so the map
                 * is being rendered inside a parent with different width and height
                 * dimensions to our final page layout. I tried various solutions but
                 * really the only consistent solution that worked was waiting 2 seconds
                 * before running the logic.
                 *
                 * It's a hack solution for now but it is low cost and fixes the issue
                 *
                 * We can address in future if needed
                 */
                await asyncTimeout(2000);
                const map = mapsByContainerName[container];
                if (!map)
                    return;
                const loadingSetter = (loading) => () => emit('mapLoading', loading);
                map.on('movestart', loadingSetter(true));
                map.on('moveend', loadingSetter(false));
                map.on('zoomstart', loadingSetter(true));
                map.on('zoomend', loadingSetter(false));
                try {
                    if (displayControl) {
                        const nav = new mapboxGl.NavigationControl();
                        map.addControl(nav, 'top-right');
                    }
                    await map.resize();
                    if (props.displayUser) {
                        addUserMapMarker(map, userLngLat.value);
                    }
                    await removeSources({
                        allSources: sources,
                        map,
                        sources,
                        groupingThreshold: props.groupingThreshold,
                    });
                    await processSources({
                        sources,
                        buildMarker: props.buildMarker,
                        disableGrouping: props.disableGrouping,
                        displayPopUp: props.displayPopUp,
                        emit,
                        map,
                        mapStyle: props.mapStyle,
                        groupingThreshold: props.groupingThreshold,
                    });
                    isLoading.value = false;
                    isAlertMapLoading.value = false;
                    fitToBounds({
                        map,
                        sources,
                        paddingBottom: props.paddingBottom,
                        customPadding: (_a = props.customPadding) !== null && _a !== void 0 ? _a : undefined,
                        displayUser: props.displayUser,
                        userLngLat: userLngLat.value,
                        maxZoomInitialTransition: props.maxZoomInitialTransition,
                    });
                }
                catch (error) {
                    console.info(error, '\nThis probably occured because the map view was unmounted prematurely');
                }
            });
            (_b = mapsByContainerName[container]) === null || _b === void 0 ? void 0 : _b.on('move', () => setCurrentBounds(container)).on('zoom', () => setCurrentBounds(container));
            setCurrentBounds(container);
            (_c = mapsByContainerName[container]) === null || _c === void 0 ? void 0 : _c.on('zoomend', () => {
                emit('zoomEnd');
            });
            Object.entries(props.callbacks).forEach(([key, value]) => {
                var _a;
                (_a = mapsByContainerName[container]) === null || _a === void 0 ? void 0 : _a.on(key, value);
            });
        };
        watch(() => props.customPadding, () => {
            Object.values(mapsByContainerName).forEach((map) => {
                var _a;
                fitToBounds({
                    map,
                    sources: props.sources,
                    paddingBottom: props.paddingBottom,
                    customPadding: (_a = props.customPadding) !== null && _a !== void 0 ? _a : undefined,
                    displayUser: props.displayUser,
                    userLngLat: userLngLat.value,
                    maxZoomInitialTransition: props.maxZoomInitialTransition,
                });
            });
        });
        onMounted(() => {
            isLoading.value = true;
        });
        const containerName = `mapContainer-${mapContainerID}`;
        let initialized = false;
        watch(mapViewContainerRef, () => {
            if (initialized)
                return;
            loadMap({
                containerName,
                displayControl: !props.expandable && !props.hideControlButtons,
            });
            initialized = true;
        });
        watch(() => store.getters['auth/getUserMapLocation'], async (location) => {
            if (!props.displayUser)
                return;
            if (!(location === null || location === void 0 ? void 0 : location.lat) && !(location === null || location === void 0 ? void 0 : location.lng)) {
                try {
                    userLngLat.value = await getCurrentPosition();
                }
                catch (error) {
                    console.error(error);
                }
            }
            else {
                userLngLat.value = location;
            }
        }, { immediate: true });
        watch([() => props.mapStyle, () => isExactTheme()], ([mapStyle]) => {
            const newStyle = getMapStyle(mapStyle);
            Object.values(mapsByContainerName).forEach((map) => {
                map.setStyle(newStyle);
            });
        }, { immediate: true });
        watch([
            /** Watch source updates */
            () => props.sources,
            /** Watch highlight updates */
            () => props.highlightID,
            /** Watch selected updates */
            () => props.selectedID,
            /** Ensure map sources are added if added during initial load */
            () => isLoading.value,
        ], ([newSources, newHighlightID, newSelectedID, newIsLoading], [prevSources, prevHighlightID, prevSelectedID, _prevIsLoading]) => {
            var _a;
            if (newIsLoading)
                return;
            const sources = getSourcesWithState(newSources);
            const onlyMarkerStateChanged = (sources.length === (prevSources === null || prevSources === void 0 ? void 0 : prevSources.length)
                && (props.highlightID !== prevHighlightID
                    || props.selectedID !== prevSelectedID));
            /** Determine which sources have been removed */
            const removedSources = (_a = prevSources === null || prevSources === void 0 ? void 0 : prevSources.filter((prevSource) => !sources.some((source) => source.id === prevSource.id))) !== null && _a !== void 0 ? _a : [];
            /** Determine which sources have been updated */
            const updatedSources = sources.filter((source) => {
                var _a, _b;
                if (isPolygonOrMultiPolygon((_a = source.data[0]) === null || _a === void 0 ? void 0 : _a.geometry)) {
                    // Maybe support dynamic updates to polygon dimensions?
                    return false;
                }
                const pointPropertiesID = (_b = getPointProperties(source)) === null || _b === void 0 ? void 0 : _b.id;
                const stateIDs = [
                    newHighlightID,
                    newSelectedID,
                    prevHighlightID,
                    prevSelectedID,
                ].filter(nullishFilter);
                const sourceStateChanged = stateIDs.includes(source.id);
                const pointPropertiesStateChanged = !!pointPropertiesID
                    && stateIDs.includes(pointPropertiesID);
                const stateChanged = (onlyMarkerStateChanged
                    && (sourceStateChanged || pointPropertiesStateChanged));
                if (stateChanged)
                    return true;
                const prevSource = prevSources === null || prevSources === void 0 ? void 0 : prevSources.find((prev) => {
                    var _a, _b;
                    return prev.id === source.id
                        && ((_a = getPointProperties(prev)) === null || _a === void 0 ? void 0 : _a.id) === ((_b = getPointProperties(source)) === null || _b === void 0 ? void 0 : _b.id);
                });
                const propertiesUpdated = !isEqual(getPointProperties(prevSource), getPointProperties(source));
                const prevCoordinates = getPointMapSourceCoordinates(prevSource);
                const sourceCoordinates = getPointMapSourceCoordinates(source);
                const [prevLng, prevLat] = roundedLatLng(prevCoordinates === null || prevCoordinates === void 0 ? void 0 : prevCoordinates[0], prevCoordinates === null || prevCoordinates === void 0 ? void 0 : prevCoordinates[1]);
                const [sourceLng, sourceLat] = roundedLatLng(sourceCoordinates === null || sourceCoordinates === void 0 ? void 0 : sourceCoordinates[0], sourceCoordinates === null || sourceCoordinates === void 0 ? void 0 : sourceCoordinates[1]);
                const isUpdated = Math.round(prevLng * 10000) !== Math.round(sourceLng * 10000)
                    || Math.round(prevLat * 10000) !== Math.round(sourceLat * 10000);
                return propertiesUpdated || isUpdated;
            });
            /** Determine which sources have been added */
            const addedSources = sources.filter((source) => !(prevSources === null || prevSources === void 0 ? void 0 : prevSources.some((prevSource) => prevSource.id === source.id)));
            if (removedSources.length
                || updatedSources.length
                || addedSources.length) {
                Object.values(mapsByContainerName).forEach(async (map) => {
                    var _a;
                    await removeSources({
                        allSources: sources,
                        map,
                        sources: [
                            ...removedSources,
                            ...updatedSources,
                        ],
                        groupingThreshold: props.groupingThreshold,
                    });
                    await processSources({
                        allSources: sources,
                        buildMarker: props.buildMarker,
                        disableGrouping: props.disableGrouping,
                        displayPopUp: props.displayPopUp,
                        emit,
                        map,
                        mapStyle: props.mapStyle,
                        sources: [
                            ...updatedSources,
                            ...addedSources,
                        ],
                        groupingThreshold: props.groupingThreshold,
                    });
                    /** Map only resizes if sources have been added or removed */
                    const sourcesOnlyUpdated = !removedSources.length && !addedSources.length
                        && updatedSources.length > 0;
                    const skipResize = onlyMarkerStateChanged || sourcesOnlyUpdated;
                    if (!skipResize) {
                        fitToBounds({
                            map,
                            sources: sources,
                            paddingBottom: props.paddingBottom,
                            customPadding: (_a = props.customPadding) !== null && _a !== void 0 ? _a : undefined,
                            displayUser: props.displayUser,
                            userLngLat: userLngLat.value,
                            maxZoomInitialTransition: props.maxZoomInitialTransition,
                        });
                    }
                });
            }
        }, { immediate: true });
        watch(() => props.displayUser, (displayUser) => {
            Object.values(mapsByContainerName).forEach((map) => {
                if (displayUser) {
                    addUserMapMarker(map, userLngLat.value);
                }
            });
        }, { immediate: true });
        const isMapAlertOpened = ref(false);
        const expandMap = async () => {
            isMapAlertOpened.value = true;
            isAlertMapLoading.value = true;
            /**
             * We need to give the Alert a moment to render so that modalMapContainer
             * can be found in the DOM, otherwise this will throw an error
             */
            await asyncTimeout(300);
            loadMap({
                containerName: `modalMapContainer-${mapContainerID}`,
                displayControl: !props.hideControlButtons,
            });
        };
        const state = {
            expandMap,
            isLoading,
            isMapAlertOpened,
            isAlertMapLoading,
            loadMap,
            mapContainerID,
            style,
            token,
            mapViewRef,
            mapViewContainerRef,
        };
        return {
            ...state,
            ...getMapViewComputedStyles(props, state),
        };
    },
});

export { script as default };
